import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { History, Search, Eye, XCircle, FileText, Calendar, User, UserCheck, ShoppingCart, Printer, ChevronLeft, ChevronRight, ShieldAlert, Lock, Mail, RotateCcw, CheckCircle, AlertTriangle, Receipt } from 'lucide-react';
import { openAnulacionTicket } from '../../utils/printTicket';
import { openComprobante } from '../../utils/printComprobante';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;
const PAGE_SIZE = 20;

export default function HistorialVentas() {
    const [ventas, setVentas] = useState([]);
    const [selectedVenta, setSelectedVenta] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);


    // Modal devolución
    const [devModal, setDevModal] = useState(null); // null | { venta }
    const [devItems, setDevItems] = useState({});   // { producto_id: cantidad }
    const [devMotivo, setDevMotivo] = useState('');
    const [devError, setDevError] = useState('');
    const [devLoading, setDevLoading] = useState(false);
    const [devResult, setDevResult] = useState(null);

    // Modal de autorización (Cajero)
    const [authModal, setAuthModal] = useState(null); // null | { ventaId }
    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authMotivo, setAuthMotivo] = useState('');

    // Modal de motivo (Admin/Supervisor)
    const [motivoModal, setMotivoModal] = useState(null); // null | { ventaId }
    const [motivoText, setMotivoText] = useState('');
    const [motivoError, setMotivoError] = useState('');

    const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userRol = currentUser.rol || (currentUser.is_admin ? 'Administrador' : 'Sin rol');
    const needsAuth = userRol === 'Cajero' || userRol === 'Sin rol';

    useEffect(() => {
        setLoading(true);
        Promise.all([
            axios.get(`${API}/ventas/`),
            axios.get(`${API}/settings/`),
        ]).then(([rv, rs]) => {
            setVentas(rv.data);
            setSettings(rs.data);
        }).catch(() => setError('Error al cargar historial.'))
          .finally(() => setLoading(false));
    }, []);

    const ventasFiltradas = ventas.filter(v => {
        const fechaVenta = new Date(v.fecha).toISOString().split('T')[0];
        const cumpleStart = !startDate || fechaVenta >= startDate;
        const cumpleEnd = !endDate || fechaVenta <= endDate;
        const cumpleSearch = !searchTerm ||
            v.id.toString().includes(searchTerm) ||
            (v.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()));
        return cumpleStart && cumpleEnd && cumpleSearch;
    });

    const totalPages = Math.max(1, Math.ceil(ventasFiltradas.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const paginated = ventasFiltradas.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const resetPage = () => setPage(1);

    const fetchDetail = async (id) => {
        try {
            const res = await axios.get(`${API}/ventas/${id}/`);
            setSelectedVenta(res.data);
        } catch {
            alert('No se pudo cargar el detalle.');
        }
    };

    const handleAnular = (id) => {
        if (needsAuth) {
            setAuthModal({ ventaId: id });
            setAuthEmail('');
            setAuthPassword('');
            setAuthMotivo('');
            setAuthError('');
        } else {
            setMotivoModal({ ventaId: id });
            setMotivoText('');
            setMotivoError('');
        }
    };

    const ejecutarAnulacion = async (ventaId, motivo, authEmailVal, authPasswordVal) => {
        const body = { motivo_anulacion: motivo };
        if (authEmailVal) {
            body.auth_email = authEmailVal;
            body.auth_password = authPasswordVal;
        }
        try {
            const res = await axios.post(`${API}/ventas/${ventaId}/anular/`, body);
            openAnulacionTicket(res.data, settings);
            const rv = await axios.get(`${API}/ventas/`);
            setVentas(rv.data);
            setSelectedVenta(null);
            setAuthModal(null);
            setMotivoModal(null);
        } catch (e) {
            const msg = e.response?.data?.error || 'Error al anular.';
            if (authEmailVal !== undefined) {
                setAuthError(msg);
            } else {
                setMotivoError(msg);
            }
        }
    };

    const handleAuthSubmit = async (e) => {
        e.preventDefault();
        if (!authMotivo.trim()) { setAuthError('Ingresá el motivo de anulación.'); return; }
        setAuthError('');
        setAuthLoading(true);
        await ejecutarAnulacion(authModal.ventaId, authMotivo, authEmail, authPassword);
        setAuthLoading(false);
    };

    const handleMotivoSubmit = async (e) => {
        e.preventDefault();
        if (!motivoText.trim()) { setMotivoError('Ingresá el motivo de anulación.'); return; }
        setMotivoError('');
        await ejecutarAnulacion(motivoModal.ventaId, motivoText, undefined, undefined);
    };

    const handleDevolver = async (venta) => {
        try {
            const res = await axios.get(`${API}/ventas/${venta.id}/`);
            const v = res.data;
            const items = {};
            v.detalles.forEach(d => {
                const disponible = d.cantidad - (d.cantidad_devuelta || 0);
                if (disponible > 0) items[d.producto] = 0;
            });
            setDevModal(v);
            setDevItems(items);
            setDevMotivo('');
            setDevError('');
            setDevResult(null);
        } catch {
            alert('No se pudo cargar el detalle de la venta.');
        }
    };

    const ejecutarDevolucion = async () => {
        if (!devMotivo.trim()) { setDevError('El motivo es obligatorio.'); return; }
        const items = Object.entries(devItems)
            .filter(([, cant]) => Number(cant) > 0)
            .map(([prod_id, cant]) => ({ producto_id: parseInt(prod_id), cantidad: Number(cant) }));
        if (items.length === 0) { setDevError('Seleccioná al menos un producto con cantidad mayor a 0.'); return; }
        setDevLoading(true);
        setDevError('');
        try {
            const res = await axios.post(
                `${API}/ventas/${devModal.id}/devolver/`,
                { motivo: devMotivo, items });
            setDevResult(res.data);
            const rv = await axios.get(`${API}/ventas/`);
            setVentas(rv.data);
        } catch (e) {
            const d = e.response?.data;
            const msg = d?.error || (Array.isArray(d?.detalles) ? d.detalles.join(', ') : d?.detalles) || 'Error al procesar devolución.';
            setDevError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setDevLoading(false);
        }
    };

    const handleFacturar = async (venta) => {
        try {
            // Obtener detalle completo de la venta
            const rv = await axios.get(`${API}/ventas/${venta.id}/`);
            const ventaFull = rv.data;

            // Emitir (o recuperar) factura
            const rf = await axios.post(`${API}/ventas/${venta.id}/facturar/`, {});
            openComprobante(ventaFull, rf.data, settings);

            // Refrescar lista para mostrar badge de factura
            const rl = await axios.get(`${API}/ventas/`);
            setVentas(rl.data);
        } catch (e) {
            const msg = e.response?.data?.error || 'Error al emitir la factura.';
            alert(msg);
        }
    };

    const statusStyle = (s) => ({
        pagado: 'bg-exito-500/20 text-exito-400 border-exito-500/30',
        anulado: 'bg-error-500/20 text-error-400 border-error-500/30',
        pendiente: 'bg-alerta-500/20 text-alerta-400 border-alerta-500/30',
    }[s] || 'bg-gray-500/20 text-gray-400 border-gray-500/30');

    return (
        <div className="space-y-6">
            {/* Header + Filtros */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 dark:border-white/10 gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-500/20 p-3 rounded-xl">
                        <History size={24} className="text-brand-800" />
                    </div>
                    <div>
                        <h2 className="titulo-modal">Historial de Ventas</h2>
                        <p className="text-sm text-slate-500 dark:text-gray-400">{ventasFiltradas.length} transacciones encontradas</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search size={16} className="icono-campo" />
                        <input
                            type="text" placeholder="Venta # o Cliente..."
                            value={searchTerm}
                            onChange={e => { setSearchTerm(e.target.value); resetPage(); }}
                            className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500"
                        />
                    </div>
                    <div className="flex items-center gap-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-1.5">
                        <Calendar size={14} className="text-slate-400 dark:text-gray-500" />
                        <input type="date" value={startDate}
                            onChange={e => { setStartDate(e.target.value); resetPage(); }}
                            className="bg-transparent text-slate-900 dark:text-white text-xs outline-none" />
                        <span className="text-slate-400 dark:text-gray-600">—</span>
                        <input type="date" value={endDate}
                            onChange={e => { setEndDate(e.target.value); resetPage(); }}
                            className="bg-transparent text-slate-900 dark:text-white text-xs outline-none" />
                    </div>
                </div>
            </div>

            {error && <div className="bg-error-500/10 border border-error-500/30 text-error-400 p-4 rounded-xl">{error}</div>}

            {/* Tabla */}
            <div className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="tabla-cabecera">
                                <th className="px-6 py-4 font-medium">ID</th>
                                <th className="px-6 py-4 font-medium">Fecha y Hora</th>
                                <th className="px-6 py-4 font-medium">Cliente</th>
                                <th className="px-6 py-4 font-medium">Vendedor</th>
                                <th className="px-6 py-4 font-medium text-center">Estado</th>
                                <th className="px-6 py-4 font-medium text-right">Total</th>
                                <th className="px-6 py-4 font-medium text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="tabla-filas">
                            {paginated.map(v => (
                                <tr key={v.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                                    <td className="px-6 py-4 font-mono text-brand-800 font-bold">#{v.id}</td>
                                    <td className="px-6 py-4 text-slate-600 dark:text-gray-300">
                                        <div className="flex flex-col">
                                            <span>{new Date(v.fecha).toLocaleDateString('es-PY')}</span>
                                            <span className="text-xs text-slate-400 dark:text-gray-500">{new Date(v.fecha).toLocaleTimeString('es-PY')}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-slate-900 dark:text-white">
                                        {v.cliente_nombre || <span className="text-slate-400 dark:text-gray-600 italic">Consumidor Final</span>}
                                    </td>
                                    <td className="px-6 py-4 text-slate-500 dark:text-gray-400">{v.vendedor_nombre || 'Sistema'}</td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${statusStyle(v.estado)}`}>
                                            {v.estado.toUpperCase()}
                                        </span>
                                        {v.tiene_devolucion && (
                                            <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-alerta-500/20 text-alerta-400 border border-alerta-500/30 font-bold">DEV</span>
                                        )}
                                        {v.tiene_factura && (
                                            <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-brand-500/20 text-brand-800 border border-brand-500/30 font-bold" title={v.nro_factura}>FAC</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right font-bold text-slate-900 dark:text-white">
                                        Gs. {parseFloat(v.total).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {v.estado === 'pagado' && (
                                                <button
                                                    onClick={() => handleFacturar(v)}
                                                    className="p-2 rounded-xl bg-brand-500/10 hover:bg-brand-500/30 text-brand-800 transition-colors"
                                                    title={v.tiene_factura ? `Reimprimir Factura ${v.nro_factura}` : 'Emitir Factura'}
                                                >
                                                    <Receipt size={18} />
                                                </button>
                                            )}
                                            {v.estado === 'pagado' && (
                                                <button
                                                    onClick={() => handleDevolver(v)}
                                                    className="p-2 rounded-xl bg-alerta-500/10 hover:bg-alerta-500/30 text-alerta-400 transition-colors"
                                                    title="Procesar Devolución"
                                                >
                                                    <RotateCcw size={18} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => fetchDetail(v.id)}
                                                className="p-2 rounded-xl bg-brand-500/10 hover:bg-brand-500/30 text-brand-800 transition-colors"
                                                title="Ver Detalle"
                                            >
                                                <Eye size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {paginated.length === 0 && !loading && (
                                <tr>
                                    <td colSpan="7" className="px-6 py-16 text-center text-slate-400 dark:text-gray-500">
                                        <FileText size={48} className="mx-auto mb-4 opacity-20" />
                                        <p>No hay ventas registradas.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-slate-200 dark:border-white/10 flex items-center justify-between">
                        <span className="text-xs text-slate-400 dark:text-gray-500">
                            Página {currentPage} de {totalPages} — {ventasFiltradas.length} registros
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-gray-400 disabled:opacity-30 transition-colors"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const pg = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                                return (
                                    <button key={pg} onClick={() => setPage(pg)}
                                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${pg === currentPage ? 'bg-brand-500 text-negro-950' : 'bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-gray-400'}`}>
                                        {pg}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-gray-400 disabled:opacity-30 transition-colors"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Autorización (solo Cajero) */}
            {authModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-slate-200 dark:border-white/10 bg-error-50 dark:bg-error-950/30">
                            <div className="flex items-center gap-3 mb-1">
                                <ShieldAlert className="text-error-500" size={24} />
                                <h3 className="titulo-item">Autorización Requerida</h3>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-gray-400 ml-9">
                                Solo un <strong>Supervisor</strong> o <strong>Administrador</strong> puede autorizar anulaciones.
                            </p>
                        </div>

                        <form onSubmit={handleAuthSubmit} autoComplete="off" className="p-6 space-y-4">
                            {authError && (
                                <div className="bg-error-500/10 border border-error-500/20 text-error-500 dark:text-error-400 px-4 py-3 rounded-xl text-sm font-medium">
                                    {authError}
                                </div>
                            )}

                            <div>
                                <label htmlFor="historial-motivo-de-anulacion" className="etiqueta-campo">
                                    Motivo de anulación
                                </label>
                                <textarea id="historial-motivo-de-anulacion"
                                    required
                                    rows={2}
                                    value={authMotivo}
                                    onChange={e => setAuthMotivo(e.target.value)}
                                    placeholder="Ej: Error en precio, producto incorrecto..."
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-error-500/40"
                                />
                            </div>

                            <div>
                                <label htmlFor="historial-correo-del-supervisor-admin" className="etiqueta-campo">
                                    Correo del Supervisor / Admin
                                </label>
                                <div className="relative">
                                    <Mail size={15} className="icono-campo" />
                                    <input id="historial-correo-del-supervisor-admin"
                                        type="text"
                                        inputMode="email"
                                        required
                                        autoFocus
                                        autoComplete="off"
                                        name="auth-supervisor-email"
                                        value={authEmail}
                                        onChange={e => setAuthEmail(e.target.value)}
                                        placeholder="supervisor@correo.com"
                                        className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-error-500/40"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="historial-contrasena-de-autorizacion" className="etiqueta-campo">
                                    Contraseña de autorización
                                </label>
                                <div className="relative">
                                    <Lock size={15} className="icono-campo" />
                                    <input id="historial-contrasena-de-autorizacion"
                                        type="password"
                                        required
                                        autoComplete="new-password"
                                        name="auth-supervisor-password"
                                        value={authPassword}
                                        onChange={e => setAuthPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-error-500/40"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setAuthModal(null)}
                                    className="flex-1 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors font-semibold text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={authLoading}
                                    className="flex-1 py-2.5 rounded-xl bg-error-500 hover:bg-error-600 disabled:opacity-50 text-white font-bold text-sm transition-colors"
                                >
                                    {authLoading ? 'Verificando...' : 'Autorizar Anulación'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Motivo (Admin/Supervisor) */}
            {motivoModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-slate-200 dark:border-white/10 bg-error-50 dark:bg-error-950/30">
                            <div className="flex items-center gap-3 mb-1">
                                <XCircle className="text-error-500" size={24} />
                                <h3 className="titulo-item">Anular Venta</h3>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-gray-400 ml-9">
                                Esta acción devolverá el stock al inventario e imprimirá una nota de anulación.
                            </p>
                        </div>
                        <form onSubmit={handleMotivoSubmit} className="p-6 space-y-4">
                            {motivoError && (
                                <div className="bg-error-500/10 border border-error-500/20 text-error-500 dark:text-error-400 px-4 py-3 rounded-xl text-sm font-medium">
                                    {motivoError}
                                </div>
                            )}
                            <div>
                                <label htmlFor="historial-motivo-de-anulacion-2" className="etiqueta-campo">
                                    Motivo de anulación *
                                </label>
                                <textarea id="historial-motivo-de-anulacion-2"
                                    required
                                    rows={3}
                                    autoFocus
                                    value={motivoText}
                                    onChange={e => setMotivoText(e.target.value)}
                                    placeholder="Ej: Error en precio, producto incorrecto, solicitud del cliente..."
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-error-500/40"
                                />
                            </div>
                            <div className="flex gap-3 pt-1">
                                <button type="button" onClick={() => setMotivoModal(null)}
                                    className="flex-1 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors font-semibold text-sm">
                                    Cancelar
                                </button>
                                <button type="submit"
                                    className="flex-1 py-2.5 rounded-xl bg-error-500 hover:bg-error-600 text-white font-bold text-sm transition-colors">
                                    Confirmar Anulación
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Devolución */}
            {devModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-200 dark:border-white/10 bg-alerta-50 dark:bg-alerta-950/30 flex justify-between items-start">
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <RotateCcw className="text-alerta-500" size={22} />
                                    <h3 className="titulo-item">Devolución — Venta #{devModal.id}</h3>
                                </div>
                                <p className="text-sm text-slate-500 dark:text-gray-400 ml-9">Indicá las cantidades a devolver. El stock se restaurará automáticamente.</p>
                            </div>
                            <button onClick={() => setDevModal(null)} className="p-1.5 rounded-full hover:bg-alerta-100 dark:hover:bg-white/10 text-slate-400 transition-colors">
                                <XCircle size={20} />
                            </button>
                        </div>

                        {devResult ? (
                            <div className="p-8 flex flex-col items-center gap-4 text-center">
                                <CheckCircle size={56} className="text-exito-400" />
                                <div>
                                    <p className="titulo-modal">Devolución Procesada</p>
                                    <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">Total devuelto: <strong className="text-alerta-400">Gs. {parseFloat(devResult.total_devuelto).toLocaleString()}</strong></p>
                                </div>
                                <button
                                    onClick={() => setDevModal(null)}
                                    className="px-6 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-negro-950 font-bold text-sm transition-colors"
                                >
                                    Cerrar
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col overflow-hidden">
                                <div className="p-6 overflow-y-auto flex-1 space-y-4">
                                    {devError && (
                                        <div className="bg-error-500/10 border border-error-500/20 text-error-400 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
                                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                            {devError}
                                        </div>
                                    )}

                                    {/* Aviso devolución total */}
                                    {devModal.detalles && (() => {
                                        const esTotal = devModal.detalles.every(d => {
                                            const disponible = d.cantidad - (d.cantidad_devuelta || 0);
                                            if (disponible <= 0) return true;
                                            return Number(devItems[d.producto] ?? 0) >= disponible;
                                        });
                                        return esTotal ? (
                                            <div className="bg-error-500/10 border border-error-500/20 text-error-400 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
                                                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                                <span>Estás devolviendo <strong>todos los ítems</strong>. Para devolución total usá <strong>Anular Venta</strong>, que gestiona correctamente la anulación del comprobante.</span>
                                            </div>
                                        ) : null;
                                    })()}

                                    <div className="space-y-2">
                                        {devModal.detalles?.map(d => {
                                            const disponible = d.cantidad - (d.cantidad_devuelta || 0);
                                            if (disponible <= 0) return (
                                                <div key={d.producto} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 opacity-40">
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-900 dark:text-white line-through">{d.producto_nombre}</p>
                                                        <p className="text-xs text-slate-400">Ya devuelto totalmente</p>
                                                    </div>
                                                </div>
                                            );
                                            return (
                                                <div key={d.producto} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{d.producto_nombre}</p>
                                                        <p className="text-xs text-slate-400">
                                                            Vendido: {d.cantidad} un.
                                                            {d.cantidad_devuelta > 0 && <span className="text-alerta-400"> · Ya devuelto: {d.cantidad_devuelta}</span>}
                                                            <span className="text-exito-400"> · Disponible: {disponible}</span>
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2 ml-3">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            max={disponible}
                                                            value={devItems[d.producto] ?? 0}
                                                            onChange={e => {
                                                                const val = Math.min(disponible, Math.max(0, parseInt(e.target.value) || 0));
                                                                setDevItems(prev => ({ ...prev, [d.producto]: val }));
                                                            }}
                                                            className="w-20 px-2 py-1.5 rounded-lg bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm text-center focus:outline-none focus:ring-2 focus:ring-alerta-500/40"
                                                        />
                                                        <span className="text-xs text-slate-400">/ {disponible}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div>
                                        <label htmlFor="historial-motivo-de-devolucion" className="etiqueta-campo">
                                            Motivo de devolución *
                                        </label>
                                        <textarea id="historial-motivo-de-devolucion"
                                            rows={3}
                                            autoFocus
                                            value={devMotivo}
                                            onChange={e => setDevMotivo(e.target.value)}
                                            placeholder="Ej: Producto defectuoso, talle incorrecto, cambio de opinión..."
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-alerta-500/40"
                                        />
                                    </div>
                                </div>

                                <div className="p-6 border-t border-slate-200 dark:border-white/10 flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setDevModal(null)}
                                        className="flex-1 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors font-semibold text-sm"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={ejecutarDevolucion}
                                        disabled={devLoading}
                                        className="flex-1 py-2.5 rounded-xl bg-alerta-500 hover:bg-alerta-600 disabled:opacity-50 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2"
                                    >
                                        {devLoading ? 'Procesando...' : <><RotateCcw size={16} /> Confirmar Devolución</>}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal Detalle */}
            {selectedVenta && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-200 dark:border-white/10 flex justify-between items-center bg-slate-50 dark:bg-gray-800/40">
                            <div>
                                <h3 className="titulo-modal">
                                    <FileText className="text-brand-800" size={24} />
                                    Venta #{selectedVenta.id}
                                </h3>
                                <div className="flex gap-4 mt-2">
                                    <span className="text-xs text-slate-500 dark:text-gray-400 flex items-center gap-1">
                                        <Calendar size={12} /> {new Date(selectedVenta.fecha).toLocaleString('es-PY')}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-gray-400 flex items-center gap-1">
                                        <UserCheck size={12} /> {selectedVenta.vendedor_nombre || 'Sistema'}
                                    </span>
                                </div>
                            </div>
                            <button onClick={() => setSelectedVenta(null)} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-gray-400 transition-colors">
                                <XCircle size={22} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-6">
                            <div className="bg-slate-50 dark:bg-white/5 rounded-2xl p-4 border border-slate-200 dark:border-white/10">
                                <h4 className="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase mb-3 flex items-center gap-2">
                                    <User size={14} /> Cliente
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-slate-400 dark:text-gray-500">Nombre</p>
                                        <p className="text-slate-900 dark:text-white font-medium">{selectedVenta.cliente_nombre || 'Consumidor Final'}</p>
                                    </div>
                                    {selectedVenta.cliente_ruc && (
                                        <div>
                                            <p className="text-xs text-slate-400 dark:text-gray-500">RUC/CI</p>
                                            <p className="text-slate-900 dark:text-white font-medium font-mono">{selectedVenta.cliente_ruc}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase mb-3 flex items-center gap-2">
                                    <ShoppingCart size={14} className="text-brand-800" /> Productos Vendidos
                                </h4>
                                <div className="space-y-2">
                                    {selectedVenta.detalles?.map((d, i) => (
                                        <div key={i} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-slate-900 dark:text-white">{d.producto_nombre}</p>
                                                <p className="text-xs text-slate-400 dark:text-gray-500">{d.cantidad} un. × Gs. {parseFloat(d.precio_unitario).toLocaleString()}</p>
                                            </div>
                                            <p className="text-sm font-bold text-brand-800">Gs. {parseFloat(d.subtotal).toLocaleString()}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Datos de pago adicionales */}
                            {selectedVenta.datos_pago && Object.keys(selectedVenta.datos_pago).length > 0 && (
                                <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
                                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase mb-2">
                                        Datos del comprobante de pago
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {Object.entries(selectedVenta.datos_pago).map(([k, v]) => v ? (
                                            <div key={k}>
                                                <p className="text-[10px] text-slate-400 uppercase tracking-wide">
                                                    {k.replace(/_/g, ' ')}
                                                </p>
                                                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                                    {k === 'nro_tarjeta' ? `****${v}` : v}
                                                </p>
                                            </div>
                                        ) : null)}
                                    </div>
                                </div>
                            )}

                            {selectedVenta.estado === 'anulado' && selectedVenta.motivo_anulacion && (
                                <div className="bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 rounded-2xl p-4">
                                    <p className="text-xs font-bold text-error-400 uppercase mb-1">Motivo de anulación</p>
                                    <p className="text-sm text-error-700 dark:text-error-300">{selectedVenta.motivo_anulacion}</p>
                                </div>
                            )}

                            {selectedVenta.devoluciones?.length > 0 && (
                                <div className="bg-alerta-50 dark:bg-alerta-500/10 border border-alerta-200 dark:border-alerta-500/20 rounded-2xl p-4">
                                    <p className="text-xs font-bold text-alerta-500 uppercase mb-3 flex items-center gap-1">
                                        <RotateCcw size={13} /> Devoluciones Registradas
                                    </p>
                                    <div className="space-y-2">
                                        {selectedVenta.devoluciones.map(dev => (
                                            <div key={dev.id} className="flex justify-between items-start text-sm">
                                                <div>
                                                    <p className="text-slate-700 dark:text-slate-300 font-medium">{dev.motivo}</p>
                                                    <p className="text-xs text-slate-400">{new Date(dev.fecha).toLocaleString('es-PY')} — {dev.procesado_por_nombre || 'Sistema'}</p>
                                                </div>
                                                <span className="font-bold text-alerta-500 whitespace-nowrap ml-4">
                                                    Gs. {parseFloat(dev.total_devuelto).toLocaleString()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center p-6 bg-brand-500/10 rounded-2xl border border-brand-500/20">
                                <div>
                                    <p className="text-xs text-brand-800 font-bold uppercase tracking-widest">Total de la Venta</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-3xl font-bold text-slate-900 dark:text-white">Gs. {parseFloat(selectedVenta.total).toLocaleString()}</p>
                                    <span className={`px-2 py-0.5 rounded-lg text-xs font-bold border ${statusStyle(selectedVenta.estado)}`}>
                                        {selectedVenta.estado.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-200 dark:border-white/10 flex justify-between items-center gap-3 flex-wrap">
                            <button
                                onClick={() => setSelectedVenta(null)}
                                className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-all text-sm font-semibold"
                            >
                                Cerrar
                            </button>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => openComprobante(selectedVenta, null, settings)}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-900 dark:text-white border border-slate-200 dark:border-white/10 transition-all text-sm font-bold"
                                >
                                    <Printer size={16} />
                                    Reimprimir
                                </button>
                                {selectedVenta.estado === 'pagado' && (
                                    <button
                                        onClick={() => handleFacturar(selectedVenta)}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-500/10 hover:bg-brand-500 text-brand-800 hover:text-negro-950 border border-brand-500/20 transition-all text-sm font-bold"
                                    >
                                        <Receipt size={16} />
                                        {selectedVenta.factura ? 'Reimprimir Factura' : 'Emitir Factura'}
                                    </button>
                                )}
                                {selectedVenta.estado === 'pagado' && (
                                    <button
                                        onClick={() => { setSelectedVenta(null); handleDevolver(selectedVenta); }}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-alerta-500/10 hover:bg-alerta-500 text-alerta-400 hover:text-white border border-alerta-500/20 transition-all text-sm font-bold"
                                    >
                                        <RotateCcw size={16} />
                                        Devolver
                                    </button>
                                )}
                                {selectedVenta.estado !== 'anulado' && (
                                    <button
                                        onClick={() => handleAnular(selectedVenta.id)}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-error-500/10 hover:bg-error-500 text-error-400 hover:text-white border border-error-500/20 transition-all text-sm font-bold"
                                    >
                                        <XCircle size={16} />
                                        Anular
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
